import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowRight, Shield, MapPin, Clock, Sun } from "lucide-react";
import { SunMark } from "../components/SunMark";
import { Marquee } from "../components/Marquee";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { CtaBand } from "../components/CtaBand";
import { Seo, faqJsonLd } from "../components/Seo";
import { T, paths, PHONE_HREF, EMAIL_HREF } from "../i18n";
import { SERVICES } from "../data/services";
import { ARTICLES } from "../data/articles";
import { CITIES } from "../data/cities";

const IMG = {
  guard: "https://images.pexels.com/photos/27299956/pexels-photo-27299956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  patrol: "https://images.unsplash.com/photo-1760292883342-419d32af1fe6?crop=entropy&cs=srgb&fm=jpg&q=85",
  skyline: "https://images.unsplash.com/photo-1594825505684-5a5a5b18b544?crop=entropy&cs=srgb&fm=jpg&q=85",
  sun: "https://images.unsplash.com/photo-1788701941552-c541256e95a3?crop=entropy&cs=srgb&fm=jpg&q=85"
};

const COPY = {
  en: {
    eyebrow: "100% First Nations Owned · Quebec",
    h1a: "Security guard",
    h1b: "services",
    h1c: "across Quebec",
    lead: "1st Nations Security is an Indigenous-owned security guard company delivering BSP-licensed guards, mobile patrols, event security and fire watch in 60+ Quebec cities — security guards in Montreal, a security company Quebec City institutions trust, and patrol coverage from Gatineau to Sept-Îles.",
    stats: [["60+", "Quebec cities covered"], ["14", "Specialized services"], ["24/7", "Bilingual dispatch"], ["100%", "First Nations owned"]],
    indigH2: "Protection Rooted in Community",
    indigP1: "1st Nations Security was founded on a simple principle: the communities being protected should share in the protection. As a 100% First Nations owned security guard company, we recruit, train and mentor Indigenous security professionals and deploy them across Quebec — in cities, industrial sites and remote northern communities alike.",
    indigP2: "Every contract you award us circulates value back into First Nations communities through employment, training and supplier relationships. Professional security, with purpose.",
    howH2: "How Deployment Works",
    how: [["Call or email", "Reach our 24/7 bilingual dispatch at +1 807-770-0243 or admin@1stnation-security.com."], ["Free site assessment", "A supervisor walks your site, documents risks and writes post orders."], ["Guards deployed", "BSP-licensed guards or patrol units start — often the same day."], ["Verified reporting", "Every shift produces GPS-verified digital reports you can review."]],
    citiesH2lead: "Click any city for its dedicated security services page."
  },
  fr: {
    eyebrow: "100 % propriété des Premières Nations · Québec",
    h1a: "Services de gardiens",
    h1b: "de sécurité",
    h1c: "partout au Québec",
    lead: "1st Nations Security est une agence de sécurité autochtone offrant gardiens licenciés BSP, patrouille mobile, sécurité événementielle et surveillance incendie dans plus de 60 villes — votre agence de sécurité à Laval comme à Longueuil, de Montréal jusqu'à Sept-Îles.",
    stats: [["60+", "villes du Québec couvertes"], ["14", "services spécialisés"], ["24/7", "répartition bilingue"], ["100 %", "propriété des Premières Nations"]],
    indigH2: "Une protection enracinée dans la communauté",
    indigP1: "1st Nations Security est fondée sur un principe simple : les communautés protégées devraient participer à la protection. À titre de compagnie de gardiens de sécurité détenue à 100 % par les Premières Nations, nous recrutons, formons et mentorons des professionnels autochtones de la sécurité et les déployons partout au Québec — en ville, sur les sites industriels et dans les communautés nordiques éloignées.",
    indigP2: "Chaque contrat que vous nous confiez fait circuler de la valeur dans les communautés des Premières Nations par l'emploi, la formation et les relations fournisseurs. Une sécurité professionnelle, avec une mission.",
    howH2: "Comment se déroule le déploiement",
    how: [["Appelez ou écrivez", "Joignez notre répartition bilingue 24/7 au +1 807-770-0243 ou admin@1stnation-security.com."], ["Évaluation gratuite du site", "Un superviseur visite votre site, documente les risques et rédige les consignes."], ["Gardiens déployés", "Gardiens licenciés BSP ou unités de patrouille — souvent le jour même."], ["Rapports vérifiés", "Chaque quart produit des rapports numériques géolocalisés que vous pouvez consulter."]],
    citiesH2lead: "Cliquez sur une ville pour accéder à sa page de services de sécurité."
  }
};

export default function Home({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const c = COPY[lang];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div data-testid="home-page">
      <Seo
        lang={lang}
        title={t.homeTitle}
        description={lang === "fr"
          ? "Agents de sécurité, patrouille mobile et sécurité d'événements partout au Québec. Demandez un plan pour Montréal, Laval, Longueuil ou Québec."
          : "Security guards, mobile patrols and event security across Quebec. Ask about coverage in Montreal, Laval, Longueuil and Quebec City."}
        enPath="/"
        frPath="/fr"
        jsonLd={faqJsonLd(t.homeFaq)}
      />

      <motion.div
        className="border-b border-slate-800/60 bg-white"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="ccib-member-banner"
      >
        <div className="mx-auto flex max-w-7xl justify-center px-4 py-3 sm:px-6 lg:px-8">
          <img
            src="/ccib-member.png"
            alt={lang === "fr" ? "Fier membre du Conseil canadien des entreprises autochtones — Entreprise autochtone certifiée CCIB" : "A proud member of the Canadian Council for Indigenous Business — CCIB Certified Indigenous Business"}
            className="h-16 w-auto object-contain sm:h-20"
            data-testid="ccib-member-badge"
          />
        </div>
      </motion.div>

      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div className="pointer-events-none absolute -top-40 left-1/3 h-[500px] w-[500px] rounded-full bg-amber-500/15 blur-[140px]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:pt-24">
          <div>
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-amber-300" data-testid="hero-eyebrow">
              <Sun size={13} /> {c.eyebrow}
            </p>
            <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-h1">
              <MaskedLine delay={0.1}>{c.h1a}</MaskedLine>
              <MaskedLine delay={0.25} className="text-amber-400 glow-text">{c.h1b}</MaskedLine>
              <MaskedLine delay={0.4}>{c.h1c}</MaskedLine>
            </h1>
            <motion.p
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
            >
              {c.lead}
            </motion.p>
            <motion.div
              className="mt-8 flex flex-col gap-4 sm:flex-row"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
            >
              <a href={PHONE_HREF} className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-bold text-obsidian transition-all hover:bg-amber-300 hover:shadow-[0_0_36px_rgba(245,124,0,0.5)]" data-testid="hero-call-button">
                <Phone size={17} /> {t.callNow}
              </a>
              <a href={EMAIL_HREF} className="flex items-center justify-center gap-2 rounded-full border border-slate-600 px-7 py-3.5 font-semibold text-slate-200 transition-colors hover:border-amber-500/60 hover:text-amber-300" data-testid="hero-email-button">
                <Mail size={17} /> {t.emailUs}
              </a>
            </motion.div>
            <motion.div
              className="mt-8 flex flex-wrap gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.7 }}
            >
              {[t.heroBadge1, t.heroBadge2, t.heroBadge3].map((b) => (
                <span key={b} className="rounded-full border border-slate-700/80 bg-card/70 px-3 py-1 text-xs text-slate-300" data-testid="hero-badge">{b}</span>
              ))}
            </motion.div>
          </div>
          <div className="relative hidden lg:block">
            <motion.div style={{ y: sunY }} className="absolute -left-16 -top-10 opacity-90">
              <SunMark size={190} />
            </motion.div>
            <motion.div style={{ y: imgY }} className="relative ml-16 overflow-hidden rounded-3xl border border-amber-500/20 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <img src={IMG.guard} alt={lang === "fr" ? "Gardien de sécurité professionnel en service au Québec" : "Professional security guard on duty in Quebec"} className="h-[430px] w-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-2xl border border-slate-700/60 bg-obsidian/80 px-4 py-3 backdrop-blur-md">
                <Shield size={18} className="text-amber-400" />
                <span className="text-sm font-semibold text-slate-200">{lang === "fr" ? "Gardiens licenciés BSP — en service maintenant" : "BSP-licensed guards — on duty now"}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={t.marquee} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="stats-section">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {c.stats.map(([n, label], i) => (
            <Reveal key={label} delay={i * 0.08} className="rounded-2xl border border-slate-800/70 bg-card/60 p-6 text-center">
              <div className="font-display text-4xl font-black text-amber-400">{n}</div>
              <div className="mt-1 text-sm text-slate-400">{label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-services">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400">{t.nav.services}</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.servicesTitle}</h2>
          <p className="mt-3 max-w-2xl text-base text-slate-400">{t.servicesSub}</p>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={(i % 4) * 0.07}>
                <Link to={p.service(s.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-card/60 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_16px_40px_rgba(245,124,0,0.12)]" data-testid={`service-card-${s.slug}`}>
                  <Icon size={26} className="text-amber-400" />
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-100 group-hover:text-amber-300">{s[lang].name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s[lang].short}</p>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-amber-400">{t.viewService} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="relative overflow-hidden py-20" data-testid="indigenous-section">
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <img src={IMG.sun} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/60" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400">{t.indigenousTitle}</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{c.indigH2}</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300">{c.indigP1}</p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{c.indigP2}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-cities">
        <Reveal>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400"><MapPin size={12} className="mr-1 inline" />{t.nav.locations}</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.citiesTitle}</h2>
              <p className="mt-3 max-w-2xl text-base text-slate-400">{t.citiesSub} {c.citiesH2lead}</p>
            </div>
            <Link to={p.cities} className="hidden shrink-0 items-center gap-2 rounded-full border border-amber-500/50 px-5 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 sm:flex" data-testid="home-all-cities-button">
              {t.nav.locations} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-2.5">
          {CITIES.slice(0, 36).map((city, i) => (
            <Reveal key={city.slug} delay={(i % 9) * 0.04}>
              <Link to={p.city(city.slug)} className="inline-block rounded-full border border-slate-800 bg-card/60 px-4 py-2 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-300" data-testid={`home-city-${city.slug}`}>
                {lang === "fr" ? city.fr : city.en}
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <Link to={p.cities} className="inline-block rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="home-cities-more">
              +{CITIES.length - 36} →
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="how-section">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400"><Clock size={12} className="mr-1 inline" />24/7</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{c.howH2}</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.how.map(([h, d], i) => (
            <Reveal key={h} delay={i * 0.08} className="relative rounded-2xl border border-slate-800/70 bg-card/60 p-6">
              <div className="font-mono text-sm text-amber-400">0{i + 1}</div>
              <h3 className="mt-3 font-display text-lg font-bold">{h}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-800/60 bg-card/30 py-16" data-testid="home-guides">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between gap-6">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400">{t.nav.guides}</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{lang === "fr" ? "Guides et conseils en sécurité" : "Security Guides & Insights"}</h2>
              </div>
              <Link to={p.guides} className="hidden shrink-0 items-center gap-2 rounded-full border border-amber-500/50 px-5 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 sm:flex" data-testid="home-all-guides-button">
                {t.nav.guides} <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {ARTICLES.slice(0, 3).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={p.guide(a.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-obsidian/60 p-6 transition-all hover:-translate-y-1 hover:border-amber-500/40" data-testid={`home-guide-${a.slug}`}>
                  <span className="self-start rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">{a.category[lang]}</span>
                  <h3 className="mt-4 flex-1 font-display text-base font-bold leading-snug text-slate-100 group-hover:text-amber-300">{a[lang].title}</h3>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-amber-400">{lang === "fr" ? "Lire" : "Read"} <ArrowRight size={12} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Faq items={t.homeFaq} title={t.faqTitle} />
      <CtaBand lang={lang} />
    </div>
  );
}
